import { injectable } from "inversify";
import {
  ApiException,
  CPLAuthTokenModel,
  CPLConnectSessionTokensModel,
  ExternalAccessTokenModel,
} from "./api/cplauthapi/CPLAuthAPI";
import { PermissionTokenModel } from "./classes/PermissionTokenModel";
import { CPLError, CPLErrorTypes } from "cpl-react-common/Helpers/CustomErrors";

export interface ISessionService {
  getUserModules(): number[];
  getUserPermissionClaims(): PermissionTokenModel[];
  getCPLSessionToken(): Promise<string>;
  setSessionTokens(tokens: CPLConnectSessionTokensModel): Promise<void>;
  clearCPLAuthToken(): Promise<void>;
  setExternalAccessSessionTokens(externalAccessTokens: ExternalAccessTokenModel, singleUseToken: string): void;
}

let refreshCPLTokenPromise: Promise<boolean> | null = null;
let refreshCPLImageTokenPromise: Promise<boolean> | null = null;
let refreshCPLExternalAccessTokenPromise: Promise<void> | null = null;

enum ActiveSessionType {
  Normal = "normal",
  ExternalAccess = "externalAccess",
}

type RefreshAPITokenProps =
  | {
      body: ExternalAccessTokenModel;
      singleUseToken: string;
    }
  | {
      body: CPLAuthTokenModel;
      singleUseToken?: never;
    };

@injectable()
export class SessionService implements ISessionService {
  readonly storage: Storage = localStorage;
  readonly cplTokenKey = "cplsessiontoken";
  readonly cplRefreshTokenKey = "cplsessionrefreshtoken";
  readonly cplTokenExpiryKey = "cplsessiontokenexpiry";
  readonly cplUserPermissionsKey = "cpluserpermissions";
  readonly cplUserModulesKey = "cplusermodules";
  readonly cplImageTokenCookieName = "cplimagetoken";
  readonly cplImageRefreshTokenKey = "cplimagerefreshtoken";
  readonly cplImageTokenExpiryKey = "cplimagetokenexpiry";
  readonly cplExternalAccessTokenKey = "cplexternalaccesstoken";
  readonly cplSingleUseTokenKey = "cplsingleusetoken";
  readonly cplExternalAccessTokenExpiryKey = "cplexternalccesstokenexpiry";
  readonly activeSessionTypeKey = "cplactivesessiontype";

  private getActiveSessionType(): ActiveSessionType {
    return sessionStorage.getItem(this.activeSessionTypeKey) === ActiveSessionType.ExternalAccess
      ? ActiveSessionType.ExternalAccess
      : ActiveSessionType.Normal;
  }

  private setActiveSessionType(sessionType: ActiveSessionType): void {
    sessionStorage.setItem(this.activeSessionTypeKey, sessionType);
  }

  getUserPermissionClaims(): PermissionTokenModel[] {
    const permissions = JSON.parse(this.storage.getItem(this.cplUserPermissionsKey) ?? "[]");
    return permissions;
  }

  getUserModules(): number[] {
    const modules = JSON.parse(this.storage.getItem(this.cplUserModulesKey) ?? "[]");
    return modules;
  }

  async getCPLSessionToken(): Promise<string> {
    if (this.storage.getItem(this.cplImageRefreshTokenKey) && this.tokenExpired(this.cplImageTokenExpiryKey)) {
      if (refreshCPLImageTokenPromise === null) {
        refreshCPLImageTokenPromise = this.refreshCPLImageToken();
        const validRefresh = await refreshCPLImageTokenPromise;
        refreshCPLImageTokenPromise = null;
        if (!validRefresh) {
          throw new CPLError(CPLErrorTypes.SESSION_EXPIRED);
        }
      } else {
        await refreshCPLImageTokenPromise;
      }
    }

    if (this.getActiveSessionType() === ActiveSessionType.ExternalAccess) {
      if (
        this.storage.getItem(this.cplExternalAccessTokenKey) &&
        this.tokenExpired(this.cplExternalAccessTokenExpiryKey)
      ) {
        if (refreshCPLExternalAccessTokenPromise === null) {
          refreshCPLExternalAccessTokenPromise = this.refreshCPLExternalAccessToken();
          await refreshCPLExternalAccessTokenPromise;
          refreshCPLExternalAccessTokenPromise = null;
        } else {
          await refreshCPLExternalAccessTokenPromise;
        }
      }

      return this.storage.getItem(this.cplExternalAccessTokenKey) ?? "";
    }

    if (this.storage.getItem(this.cplRefreshTokenKey) && this.tokenExpired(this.cplTokenExpiryKey)) {
      if (refreshCPLTokenPromise === null) {
        refreshCPLTokenPromise = this.refreshCPLToken();
        const validRefresh = await refreshCPLTokenPromise;
        refreshCPLTokenPromise = null;
        if (!validRefresh) {
          throw new CPLError(CPLErrorTypes.SESSION_EXPIRED);
        }
      } else {
        await refreshCPLTokenPromise;
      }
    }

    return this.storage.getItem(this.cplTokenKey) ?? "";
  }

  async setSessionTokens(tokens: CPLConnectSessionTokensModel): Promise<void> {
    this.setActiveSessionType(ActiveSessionType.Normal);
    this.setCPLToken(tokens.cplapiToken);
    await this.setCPLImageToken(tokens.imageToken);
  }

  async clearCPLAuthToken(): Promise<void> {
    sessionStorage.removeItem(this.activeSessionTypeKey);
    this.storage.removeItem(this.cplTokenKey);
    this.storage.removeItem(this.cplRefreshTokenKey);
    this.storage.removeItem(this.cplTokenExpiryKey);
    this.storage.removeItem(this.cplUserPermissionsKey);
    this.storage.removeItem(this.cplUserModulesKey);
    this.storage.removeItem(this.cplExternalAccessTokenKey);
    this.storage.removeItem(this.cplSingleUseTokenKey);
    this.storage.removeItem(this.cplExternalAccessTokenExpiryKey);
    const currentImageToken = this.storage.getItem(this.cplImageTokenCookieName);
    this.storage.removeItem(this.cplImageRefreshTokenKey);
    this.storage.removeItem(this.cplImageTokenExpiryKey);
    this.storage.removeItem(this.cplImageTokenCookieName);
    if (currentImageToken !== null) {
      await this.deleteImageCookie(currentImageToken);
    }
  }

  setExternalAccessSessionTokens(externalAccessTokens: ExternalAccessTokenModel, singleUseToken: string): void {
    this.setActiveSessionType(ActiveSessionType.ExternalAccess);
    this.storage.setItem(this.cplExternalAccessTokenKey, externalAccessTokens.token ?? "");
    this.storage.setItem(this.cplSingleUseTokenKey, singleUseToken);
    this.setTokenExpiry(this.cplExternalAccessTokenExpiryKey, externalAccessTokens.expiresIn);
  }

  private async refreshCPLToken(): Promise<boolean> {
    const token = this.storage.getItem(this.cplTokenKey) ?? "";
    const refreshToken = this.storage.getItem(this.cplRefreshTokenKey) ?? "";
    const tokens = await this.refreshApiToken({
      body: new CPLAuthTokenModel({ token, refreshToken }),
    });
    if (tokens == null) {
      // Session expired
      return false;
    }
    this.setCPLToken(tokens);
    return true;
  }

  private async refreshCPLImageToken(): Promise<boolean> {
    const token = this.storage.getItem(this.cplImageTokenCookieName) ?? "";
    const refreshToken = this.storage.getItem(this.cplImageRefreshTokenKey) ?? "";
    const tokens = await this.refreshApiToken({
      body: new CPLAuthTokenModel({ token, refreshToken }),
    });
    if (tokens == null) {
      // Session expired
      return false;
    }
    await this.setCPLImageToken(tokens);
    return true;
  }

  private async refreshCPLExternalAccessToken(): Promise<void> {
    const token = this.storage.getItem(this.cplExternalAccessTokenKey) ?? "";
    const singleUseToken = this.storage.getItem(this.cplSingleUseTokenKey) ?? "";
    const tokens = await this.refreshApiToken({
      body: new ExternalAccessTokenModel({ token }),
      singleUseToken,
    });
    return this.setExternalAccessSessionTokens(tokens, singleUseToken);
  }

  private async refreshApiToken({ body, singleUseToken }: RefreshAPITokenProps): Promise<CPLAuthTokenModel> {
    const url = `${process.env.REACT_APP_CPL_AUTH_API_BASE_URI}/api/Authentication/${
      singleUseToken === undefined
        ? "RefreshTokenForCPLUser"
        : `RefreshTokenForCPLExternalAccessUser?singleUseToken=${encodeURIComponent(singleUseToken)}`
    }`;

    const content = JSON.stringify(body);
    const options: RequestInit = {
      body: content,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/plain",
        Timeout: "300",
      },
    };

    const response = await fetch(url, options);
    const cplAuthTokenModel = await this.processTokenRefreshResponse(
      response,
      singleUseToken === undefined ? CPLAuthTokenModel.fromJS : ExternalAccessTokenModel.fromJS
    );
    return cplAuthTokenModel;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private async processTokenRefreshResponse<T extends CPLAuthTokenModel | ExternalAccessTokenModel>(
    response: Response,
    fromJS: (data: any) => T
  ): Promise<T> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      const _responseText = await response.text();
      let result200: any = null;
      const resultData200 = _responseText === "" ? null : JSON.parse(_responseText);
      result200 = fromJS(resultData200);
      return result200;
    } else if (status !== 200 && status !== 204) {
      const _responseText_1 = await response.text();
      throw new ApiException("An unexpected server error occurred.", status, _responseText_1, _headers, null);
    }
    return Promise.resolve<T>(null as any);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  private setCPLToken(token: CPLAuthTokenModel | undefined) {
    this.storage.setItem(this.cplTokenKey, token?.token ?? "");
    this.storage.setItem(this.cplRefreshTokenKey, token?.refreshToken ?? "");
    this.setTokenExpiry(this.cplTokenExpiryKey, token?.expiresIn);
    this.setClaims(token?.token ?? "");
  }

  private async setCPLImageToken(token: CPLAuthTokenModel | undefined) {
    await this.setImageCookie(token?.token ?? "");
    this.storage.setItem(this.cplImageTokenCookieName, token?.token ?? "");
    this.storage.setItem(this.cplImageRefreshTokenKey, token?.refreshToken ?? "");
    this.setTokenExpiry(this.cplImageTokenExpiryKey, token?.expiresIn);
  }

  private setTokenExpiry(storageKey: string, expiresIn: number | undefined) {
    const expiry = Date.now() + (expiresIn ?? 0) * 1000;
    this.storage.setItem(storageKey, expiry.toString());
  }

  private tokenExpired(expiryKey: string): boolean {
    const expiry = parseInt(this.storage.getItem(expiryKey) ?? "0");
    return expiry < Date.now();
  }

  private setClaims(token: string) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    const claims = JSON.parse(jsonPayload);
    this.storage.setItem(this.cplUserPermissionsKey, claims.permissions);
    this.storage.setItem(this.cplUserModulesKey, claims.modules);
  }

  private async setImageCookie(value: string) {
    const endpoint = `ReflectImageCookie?imageToken=${encodeURIComponent(value)}`;
    await this.makeCookieRequest(endpoint, value);
  }

  private async deleteImageCookie(currentValue: string) {
    const endpoint = "DeleteImageCookie";
    await this.makeCookieRequest(endpoint, currentValue);
  }

  private async makeCookieRequest(endpoint: string, bearerToken: string) {
    const url = `${process.env.REACT_APP_CPL_API_BASE_URI}/api/CookieReflection/${endpoint}`;
    const options: RequestInit = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
        Accept: "text/plain",
        Timeout: "300",
      },
      credentials: "include",
    };
    await fetch(url, options);
  }
}
